/**
 * The offers nobody ever closed out in Lever — collapsed, at the bottom, with a button that asks
 * TA to tidy them (Theresa, Sep 29 2026: *"clean-up suggestions are great, but collapse them in a
 * section at the bottom and give me a button that I can click to send a slack message"*).
 *
 * ⚠️ IT IS THE OPPOSITE POPULATION FROM THE TILE ABOVE IT, which is why it is its own section
 * rather than a line on that tile. The tile counts offers on roles that are still open, in the last
 * 90 days — what somebody is waiting on. This counts every unresolved offer older than that window
 * across every posting, open or closed, because what needs tidying is precisely the records on
 * roles nobody is working any more. Measured live on 2026-09-29: 17, of which 15 sat on closed
 * postings and one on a role still being recruited.
 *
 * ⚠️ THE BUTTON SENDS NO FIGURES. The proxy re-measures them before posting — a stale tab putting
 * "47 stale offers" into a team channel is exactly the failure a message like this must not have,
 * since the only thing it is worth is that its number can be trusted.
 *
 * ⚠️ NO CANDIDATE NAMES reach Slack: counts, a date and the rule that found them. That is enough to
 * reproduce the filter in Lever, and it keeps a channel post clear of the candidate data the rest
 * of this app is careful with.
 */
import { useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { AlertCircle, Check, Loader2 } from 'lucide-react';
import type { OfferPulse } from '../../../types/openPositions';
import { sendOfferHygieneNudge } from '../../../services/openPositionsApi';
import { logAnalyticsEvent } from '../../../../../analytics/nativeAnalytics';

export function OfferHygiene({ pulse }: { pulse: OfferPulse | null | undefined }) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hygiene = pulse?.hygiene;
  // Nothing to tidy is the outcome this section exists for, so it renders nothing at all.
  if (!hygiene || hygiene.stale < 1) return null;

  const onClosed = hygiene.stale - hygiene.staleOnOpenRoles;

  async function nudge() {
    setSending(true);
    setError(null);
    try {
      await sendOfferHygieneNudge(window.location.href);
      setSent(true);
      // Counts only — never the records, never the channel's contents.
      void logAnalyticsEvent('ta_offer_hygiene_nudged', { stale: hygiene?.stale ?? 0 });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSending(false);
    }
  }

  return (
    <details data-testid="offer-hygiene" className="group rounded-xl border border-gray-200 bg-white">
      <summary className="flex cursor-pointer list-none items-center gap-2 px-5 py-3 text-sm text-gray-700 hover:bg-gray-50">
        <span
          aria-hidden
          className="inline-block h-2 w-2 border-r-[1.5px] border-b-[1.5px] border-gray-400 transition-transform duration-150 group-open:rotate-45"
          style={{ transform: 'rotate(-45deg)' }}
        />
        <span>
          <b className="font-semibold">{hygiene.stale}</b> stale{' '}
          {hygiene.stale === 1 ? 'offer' : 'offers'} in Lever
        </span>
        <span className="text-xs text-gray-400">
          never signed, never archived — not counted above
        </span>
      </summary>

      <div className="space-y-3 border-t border-gray-100 px-5 py-4 text-sm text-gray-600">
        <p>
          These opportunities reached the <b>Offer</b> stage and were neither signed nor archived, so
          they sit in the funnel indefinitely. Everything older than {pulse?.windowDays} days counts.
        </p>

        <ul className="list-disc space-y-1 pl-5">
          {hygiene.staleOnOpenRoles > 0 && (
            <li>
              <b>{hygiene.staleOnOpenRoles}</b> on a role that is still open — the{' '}
              {hygiene.staleOnOpenRoles === 1 ? 'one' : 'ones'} making Lever&apos;s offer count read
              higher than the tile above.
            </li>
          )}
          {onClosed > 0 && (
            <li>
              <b>{onClosed}</b> on roles that have since closed.
            </li>
          )}
          {hygiene.oldest && (
            <li>
              The oldest is from <b>{hygiene.oldest}</b>.
            </li>
          )}
        </ul>

        <p className="text-xs text-gray-500">
          Each one needs archiving with the right reason, or marking as signed if the hire happened.
        </p>

        <div className="flex flex-wrap items-center gap-3 pt-1">
          <Button variant="secondary" size="sm" onClick={() => void nudge()} disabled={sending || sent}>
            {sending ? (
              <span className="flex items-center gap-1.5">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Sending…
              </span>
            ) : sent ? (
              <span className="flex items-center gap-1.5">
                <Check className="h-3.5 w-3.5" />
                Sent to #ppl_talent_acquisition
              </span>
            ) : (
              'Ask TA to tidy these in Slack'
            )}
          </Button>
          {!sent && !error && (
            <span className="text-xs text-gray-500">
              Posts one message to #ppl_talent_acquisition with the counts — no candidate names.
            </span>
          )}
          {error && (
            <span className="flex items-center gap-1.5 text-xs text-rose-700">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              {error}
            </span>
          )}
        </div>
      </div>
    </details>
  );
}
